import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  lighten,
  MenuItem,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useUserData } from "components/customHooks/userDataContext";
import { getDiagnosisRisk, getRiskAdjustmentScore } from "apiHandler/actions";
import ManIcon from "@mui/icons-material/Man";
import WomanIcon from "@mui/icons-material/Woman";
import RiskToggle from "./riskToggle";

const genderOptions = [
  { icon: WomanIcon, value: "F" },
  {
    icon: ManIcon,
    value: "M",
  },
];

const eligReasonOptions = [
  { label: "Old age and survivor’s insurance- OASI (0)", value: "0" },
  { label: "Disability insurance benefits - DIB (1)", value: "1" },
  { label: "End-stage renal disease (2)", value: "2" },
  { label: "Both DIB and ESRD (3)", value: "3" },
];

const medicaidOptions = ["True", "False"];
const livingOptions = ["Community", "Institution"];
const dualStatusOptions = ["Non Dual", "Partial Dual", "Full Dual"];
const enrolleeOptions = ["Yes", "No"];

const RiskScoreFilter = ({ setData, setLoading }) => {
  const [form, setForm] = useState({
    gender: "M",
    age: 65,
    orig_medicare_elig_reason: "0",
    medicaid: "False",
    living_situation: "Community",
    dual_elig_status: "Non Dual",
    new_enrollee_status: "No",
    diagnosis_codes: [],
  });
  const [diagOptions, setDiagOptions] = useState([]);
  const [diagInputValue, setDiagInputValue] = useState("");
  const [suggestionEnabled, setSuggestionEnabled] = useState(true);
  const [manualCode, setManualCode] = useState("");
  const userData = useUserData();
  const theme = useTheme();

  const token = userData?.access_token;

  useEffect(() => {
    fetchAdjustmentScore();
  }, [form]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDiagnosisSearch = async (value) => {
    if (value === "") {
      return;
    }
    try {
      const res = await getDiagnosisRisk(
        { search_by: value, type: "Diagnosis" },
        token
      );
      setDiagOptions(res || []);
    } catch (error) {
      console.error("Error fetching diagnosis options:", error);
      setDiagOptions([]);
    }
  };

  const handleAddDiagnosis = (option) => {
    if (
      option?.code &&
      !form.diagnosis_codes.includes(option.code) &&
      form.diagnosis_codes.length < 12
    ) {
      setForm((prev) => ({
        ...prev,
        diagnosis_codes: [...prev.diagnosis_codes, option.code],
      }));

      // Delay clearing to avoid race condition with Autocomplete internals
      setTimeout(() => {
        setDiagInputValue("");
        setDiagOptions([]);
      }, 0);
    }
  };

  const removeDiagnosisCode = (codeToRemove) => {
    setForm((prev) => ({
      ...prev,
      diagnosis_codes: prev.diagnosis_codes.filter(
        (code) => code !== codeToRemove
      ),
    }));
  };

  const fetchAdjustmentScore = async () => {
    try {
      const res = await getRiskAdjustmentScore(form, token);
      if (res) {
        setData(res);
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitDiagCodes = () => {
    if (
      manualCode &&
      !form.diagnosis_codes.includes(manualCode) &&
      form.diagnosis_codes.length < 12
    ) {
      const cleanedCodes = manualCode
        .replace(/["']/g, "")
        .split(/[\s,]+/)
        .map((code) => code.trim().toUpperCase())
        .filter((code) => code);

      setForm((prev) => {
        const existing = new Set(prev.diagnosis_codes);
        const combined = [...prev.diagnosis_codes];

        for (const code of cleanedCodes) {
          if (!existing.has(code) && combined.length < 12) {
            combined.push(code);
            existing.add(code);
          }
        }

        return { ...prev, diagnosis_codes: combined };
      });
      setManualCode("");
    }
  };

  const onPasteDiagCodes = (e) => {
    const pastedText = e.clipboardData.getData("text");
    const cleanedCodes = pastedText
      .replace(/["']/g, "")
      .split(/[\s,]+/)
      .map((code) => code.trim().toUpperCase())
      .filter((code) => code);

    setForm((prev) => {
      const existing = new Set(prev.diagnosis_codes);
      const combined = [...prev.diagnosis_codes];

      for (const code of cleanedCodes) {
        if (!existing.has(code) && combined.length < 12) {
          combined.push(code);
          existing.add(code);
        }
      }

      return { ...prev, diagnosis_codes: combined };
    });

    e.preventDefault();
  };

  return (
    <Box>
      <Typography variant="h6" ml={2}>
        Risk Score Filter Criteria
      </Typography>
      <Box padding={2}>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center">
            <Box>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                Gender
              </Typography>
              {genderOptions.map((item, index) => {
                const isSelected = form.gender === item.value;
                return (
                  <IconButton
                    key={index}
                    sx={{
                      height: 50,
                      width: 50,
                      backgroundColor: isSelected
                        ? theme.palette.primary.main
                        : lighten(theme.palette.primary.main, 0.7),
                      borderRadius: 0,
                      color: "white",
                      border: isSelected
                        ? "2px solid transparent"
                        : "1px solid #ccc",
                      "&:hover": {
                        backgroundColor: isSelected
                          ? theme.palette.primary.main
                          : lighten(theme.palette.primary.main, 0.7),
                      },
                    }}
                    onClick={() => handleChange("gender", item.value)}
                  >
                    <item.icon />
                  </IconButton>
                );
              })}
            </Box>

            <Slider
              value={form.age}
              onChange={(e, value) => handleChange("age", value)}
              min={65}
              max={100}
              step={1}
              valueLabelDisplay="auto"
              sx={{ width: 200, ml: 4 }}
            />
            <Typography
              variant="caption"
              color={form.age < 65 ? "error" : "textSecondary"}
              ml={2}
            >
              {form.age < 65
                ? "Age must be 65 or older"
                : `Selected Age: ${form.age}`}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={4}>
            <Box>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                Original Medicare Eligibility Reason
              </Typography>
              <TextField
                select
                value={form.orig_medicare_elig_reason}
                onChange={(e) =>
                  handleChange("orig_medicare_elig_reason", e.target.value)
                }
                fullWidth
              >
                {eligReasonOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <RiskToggle
              title={"New Medicare Enrollee?"}
              value={form.new_enrollee_status}
              handleChange={handleChange}
              field={"new_enrollee_status"}
              data={enrolleeOptions}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            <RiskToggle
              title={"Medicaid"}
              value={form.medicaid}
              handleChange={handleChange}
              field={"medicaid"}
              data={medicaidOptions}
            />
            <RiskToggle
              title={"Living Situation"}
              value={form.living_situation}
              handleChange={handleChange}
              field={"living_situation"}
              data={livingOptions}
            />
            <RiskToggle
              title={"Dual Eligible Status"}
              value={form.dual_elig_status}
              handleChange={handleChange}
              field={"dual_elig_status"}
              data={dualStatusOptions}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            {suggestionEnabled ? (
              <Autocomplete
                freeSolo
                disableClearable
                disabled={form.diagnosis_codes.length >= 12}
                options={diagOptions.map((opt) => ({
                  ...opt,
                  disabled: form.diagnosis_codes.includes(opt.code),
                }))}
                filterOptions={(options, state) => {
                  const input = state.inputValue
                    .replace(/\./g, "")
                    .toUpperCase();
                  return options.filter((opt) => {
                    const code = opt.code?.replace(/\./g, "").toUpperCase();
                    const desc = opt.description?.toUpperCase();
                    return code?.includes(input) || desc?.includes(input);
                  });
                }}
                getOptionLabel={(option) => option.fulltext || ""}
                inputValue={diagInputValue}
                isOptionEqualToValue={(option, value) =>
                  option.code === value.code
                }
                onInputChange={(e, value) => {
                  const cleanedValue = value.replace(/["']/g, "");
                  setDiagInputValue(cleanedValue);
                  if (suggestionEnabled) handleDiagnosisSearch(cleanedValue);
                }}
                onChange={(e, value) => handleAddDiagnosis(value)}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{
                      opacity: option.disabled ? 0.5 : 1,
                      pointerEvents: option.disabled ? "none" : "auto",
                    }}
                  >
                    {option.fulltext}
                  </li>
                )}
                sx={{ width: "30%" }}
                onPaste={onPasteDiagCodes}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Diagnosis Codes"
                    fullWidth
                    variant="outlined"
                    helperText={
                      form.diagnosis_codes.length >= 12
                        ? "Maximum 12 codes allowed."
                        : ""
                    }
                  />
                )}
              />
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  value={manualCode}
                  label="Diagnosis Code"
                  variant="outlined"
                  onChange={(e) => {
                    setManualCode(e.target.value.toUpperCase());
                  }}
                  disabled={form.diagnosis_codes.length >= 12}
                  helperText={
                    form.diagnosis_codes.length >= 12
                      ? "Maximum 12 codes allowed."
                      : ""
                  }
                />
                <IconButton onClick={onSubmitDiagCodes}>➕</IconButton>
              </Box>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={suggestionEnabled}
                  onChange={() => setSuggestionEnabled((prev) => !prev)}
                  color="primary"
                />
              }
              label="Enable Suggestions"
            />
          </Box>
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
            {form.diagnosis_codes.map((code) => (
              <Chip
                key={code}
                label={code}
                onDelete={() => removeDiagnosisCode(code)}
              />
            ))}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default RiskScoreFilter;
